import type { BtlApplication } from 'api/btlApplicationForm/types';
import { btlApplicationFormApi } from 'api/btlApplicationForm';
import { App } from 'antd';
import {
  getAdjacentOoCaseSidebarItemId,
  getAdjacentOoCaseSidebarUrlSection,
  getOoCaseSidebarNavigationLabel,
} from 'components/CaseSidebar';
import type { OoCaseSidebarContext } from 'components/CaseSidebar/buildOoCaseSidebar';
import { CaseSidebarItemId } from 'components/CaseSidebar/types';
import { FormValidationContext } from 'hooks';
import { invalidateBtlApplicationData } from 'pages/BtlApplicationFormPage/btl-utils';
import { makeFormReadonly } from 'pages/BtlApplicationFormPage/makeFormReadonly';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { mapHouseholdToOoExpensesFormValues } from './mapHouseholdToOoExpensesFormValues';
import { ooExpensesFormSchema } from './ooExpensesFormSchema';

interface UseOoCaseExpensesFormParams {
  caseId: string;
  caseData?: BtlApplication;
  isCaseLoading: boolean;
  sidebarContext: OoCaseSidebarContext;
}

export function useOoCaseExpensesForm({
  caseId,
  caseData,
  isCaseLoading,
  sidebarContext,
}: UseOoCaseExpensesFormParams) {
  const { formatMessage } = useIntl();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const formValidationContext = useContext(FormValidationContext);
  const formSectionParam = searchParams.get('section') ?? CaseSidebarItemId.EXPENSES;

  const isFormEditable = caseData?.status === 'DIP_ACCEPTED' && sidebarContext.canEditDipAcceptedSections;
  const isDipAccepted = isFormEditable;
  const nextSection = getAdjacentOoCaseSidebarUrlSection(formSectionParam, sidebarContext, 'next') ?? '';

  if (isFormEditable && !formValidationContext) {
    throw new Error('OO expenses form must be used within a FormValidationProvider when editable');
  }

  const setIsSubmitting = formValidationContext?.setIsSubmitting ?? (() => undefined);

  const { data: households, isLoading: isHouseholdsLoading } = useQuery(
    ['btl-application-households', caseId],
    () => btlApplicationFormApi.getHouseholds(caseId),
    {
      enabled: Boolean(caseId) && !isCaseLoading,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const household = households?.[0];
  const householdId = household?.id;

  if (isCaseLoading || !caseId || isHouseholdsLoading) {
    return { isLoading: true as const };
  }

  const schemaProps = {
    formatMessage,
    isFormEditable,
    isDipAccepted,
    isDipAcceptedOrLater: isDipAccepted,
  };

  const formSchema = ooExpensesFormSchema(schemaProps);
  const adjustedFormSchema = isFormEditable ? formSchema : makeFormReadonly(formSchema);

  return {
    isLoading: false as const,
    isFormEditable,
    footerProps: {
      previousSection: getAdjacentOoCaseSidebarItemId(formSectionParam, sidebarContext, 'previous'),
      nextSection: getAdjacentOoCaseSidebarItemId(formSectionParam, sidebarContext, 'next'),
      previousLabel: getOoCaseSidebarNavigationLabel(formatMessage, formSectionParam, sidebarContext, 'previous'),
      nextLabel: getOoCaseSidebarNavigationLabel(formatMessage, formSectionParam, sidebarContext, 'next'),
    },
    formProps: {
      columns: adjustedFormSchema,
      isFormEditable,
      omitNil: false,
      name: 'oo_expenses',
      request: async () => mapHouseholdToOoExpensesFormValues(household),
      onFinish: isFormEditable
        ? async (values: any) => {
            try {
              setIsSubmitting(true);

              if (householdId) {
                await btlApplicationFormApi.editHousehold(caseId, householdId, values);
              } else {
                await btlApplicationFormApi.createHousehold(caseId, values);
              }

              await invalidateBtlApplicationData(queryClient, caseId);
              await queryClient.invalidateQueries({ queryKey: ['btl-application-households', caseId] });

              if (nextSection) {
                navigate({ search: `?section=${nextSection}` });
              }
            } catch (error: unknown) {
              console.error(error);
              message.error(error instanceof Error ? error.message : 'Failed to save expenses');
            } finally {
              setIsSubmitting(false);
            }
          }
        : undefined,
    },
  };
}
